import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Select,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useRef, useState, memo, useMemo } from "react";
import { HiBadgeCheck } from "react-icons/hi";
import { Calendar, DateObject } from "react-multi-date-picker";
import { DISCOUNT_RULES, WEEK_DAYS_I18N } from "../../lib/order";
import { useWindowDimensions } from "../../lib/windows";
import appStore from "../../store";
import {
  isTomorrow,
  addMinutes,
  differenceInMinutes,
  format,
  getHours,
  isBefore,
  isAfter,
  addDays,
  set,
  isSameDay,
} from "date-fns";
import { differenceInHours } from "date-fns";
import GCPImage from "components/GCPImage";
import { toast } from "react-toastify";
import toastErrorAndReport from "lib/toastErrorAndReport";
import { getNewStartDays } from "lib/common";

const TIME_OPTIONS = [
  "09",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "19",
  "20",
];

const DiscountBlock = memo((props) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const { locale } = router;
  const { diffMins } = props;
  // 取最高優惠
  const matchRule = DISCOUNT_RULES(locale)
    .filter((d) => diffMins >= d?.ms)
    .reverse()[0];
  const freeMileDays = Math.floor(diffMins / (60 * 24));
  return (
    <Box
      p="10px"
      my="20px"
      borderTop="1px solid rgba(0, 0, 0, .1)"
      borderBottom="1px solid rgba(0, 0, 0, .1)"
    >
      <Box
        p="10px"
        border={matchRule ? "2px solid #FF6F6F" : "1px solid rgba(0, 0, 0, .1)"}
        rounded="10px"
        mt="10px"
        bgColor="#FBFBFB"
      >
        <HStack justify="space-between" align="start">
          <HStack spacing="2px">
            {matchRule?.text ? (
              <HiBadgeCheck color="#FF6F6F" />
            ) : (
              <Text>＊</Text>
            )}
            <Text>{t("multiDaysDiscount")}</Text>
          </HStack>
          {matchRule?.text && (
            <Text fontSize="12px" color={"#FF6F6F"} fontWeight="bold">
              {matchRule?.text}
            </Text>
          )}
        </HStack>
        <Text textAlign="start" fontSize="12px" mt="3px">
          {t("anyTime")}
          {DISCOUNT_RULES(locale)
            .map(({ text }) => text)
            .join("｜")}
          {t("discountHighest")}
        </Text>
      </Box>
      <Box
        p="10px"
        border={
          !!freeMileDays ? "2px solid #FF6F6F" : "1px solid rgba(0, 0, 0, .1)"
        }
        rounded="10px"
        mt="10px"
        bgColor="#FBFBFB"
      >
        <HStack justify="space-between" align="start">
          <HStack spacing="2px">
            {!!freeMileDays ? (
              <HiBadgeCheck color="#FF6F6F" />
            ) : (
              <Text>＊</Text>
            )}
            <Text>{t("mileageDiscount")}</Text>
          </HStack>
          {!!freeMileDays && (
            <Text fontSize="12px" color={"#FF6F6F"} fontWeight="bold">
              {t("achieve")} {freeMileDays} {t("day")}
            </Text>
          )}
        </HStack>
        <Text textAlign="start" fontSize="12px" mt="3px">
          {t("mileageDiscountInfo")}
        </Text>
        {!!freeMileDays && (
          <Text
            textAlign="start"
            fontSize="12px"
            mt="3px"
            color={"#FF6F6F"}
            fontWeight="bold"
          >
            {t("willDicount")} {(freeMileDays * 300).toLocaleString()}{" "}
            {t("mileage")}
          </Text>
        )}
      </Box>
    </Box>
  );
});
DiscountBlock.displayName = "DiscountBlock";

const TimeSelectorContent = () => {
  const { t } = useTranslation("common");
  const { width } = useWindowDimensions();
  const router = useRouter();
  const { locale } = router;

  const {
    latestOrderTimeUnion,
    timeOptsUnion,
    selectedDays,
    selectedStartClock,
    selectedEndClock,
    tempSelectedDays,
    tempSelectedEndClock,
    tempSelectedStartClock,
    isLoading,
  } = appStore;
  const minDate = new DateObject().add(latestOrderTimeUnion, "minutes");
  const [values, setValues] = useState(selectedDays);
  const [discountText, setDiscountText] = useState("");
  const [smallDiscountText, setSmallDiscountText] = useState("");

  useEffect(() => {
    if (values.length === 2) {
      appStore.tempSelectedDays = values;
    } else {
      appStore.tempSelectedStartClock = "";
      appStore.tempSelectedEndClock = "";
      setDiscountText("");
      setSmallDiscountText("");
    }
  }, [values]);

  useEffect(() => {
    if (selectedDays.length === 2) {
      setValues(selectedDays);
    }
  }, [selectedDays]);

  useEffect(() => {
    if (tempSelectedDays.length === 2) {
      // 如果開始日期在2024/02/06-2024/02/16之間，將日期設定回預設日期
      const isBetween =
        isBefore(
          new Date(tempSelectedDays[1].format()),
          new Date("2025/02/03")
        ) &&
        isAfter(new Date(tempSelectedDays[1].format()), new Date("2025/01/23"));
      if (isBetween) {
        const newStartDays = getNewStartDays();
        appStore.tempSelectedDays = newStartDays;
        appStore.selectedDays = newStartDays;
      }
    }
  }, [tempSelectedDays]);

  useEffect(() => {
    if (tempSelectedDays.length === 2) {
      if (tempSelectedStartClock !== "" && tempSelectedEndClock !== "") {
        let diff = differenceInMinutes(
          new Date(
            tempSelectedDays[1].format("YYYY/MM/DD") +
              " " +
              tempSelectedEndClock
          ),
          new Date(
            tempSelectedDays[0].format("YYYY/MM/DD") +
              " " +
              tempSelectedStartClock
          )
        );
        if (diff < 1440) {
          appStore.tempSelectedEndClock = "";
          return alert(t("timeError"));
        }

        const matchRule = DISCOUNT_RULES(locale)
          .map((d, index) => {
            return {
              ...d,
              index: index,
            };
          })
          .filter((d) => diff >= d?.ms)
          .reverse()[0];

        const nextMatchRule = DISCOUNT_RULES(locale).filter((d, index) => {
          if (!matchRule) return index === 0;
          return index > (matchRule ? matchRule.index : 0);
        })[0];

        setSmallDiscountText("");
        setDiscountText(
          matchRule
            ? locale === "zh-TW"
              ? `🥳你已獲得${matchRule.text}優惠！${
                  nextMatchRule
                    ? `再多租${nextMatchRule?.ms / 60 - diff / 60}小時可享 ${
                        nextMatchRule.rate * 10
                      }折優惠！`
                    : ""
                }`
              : `🥳You've received a ${Math.round(
                  (1.0 - matchRule.rate) * 100
                )}% discount for ${
                  matchRule?.ms / 60 / 24
                } full days! Rent for an additional ${
                  nextMatchRule?.ms / 60 - diff / 60
                } hours to enjoy a ${Math.round(
                  (1 - nextMatchRule.rate) * 100
                )} discount!`
            : locale === "zh-TW"
              ? // 再多租Ｘ小時可享X折優惠
                `再多租${nextMatchRule?.ms / 60 - diff / 60}小時可享 ${
                  nextMatchRule.rate * 10
                }折優惠！`
              : //Rent for an additional 24 hours to enjoy a 20% discount!
                `Rent for an additional ${
                  nextMatchRule?.ms / 60 - diff / 60
                } hours to enjoy a ${Math.round(
                  (1 - nextMatchRule.rate) * 100
                )} discount!`
        );
      }
      if (
        values.length === 2 &&
        tempSelectedStartClock !== "" &&
        tempSelectedEndClock === ""
      ) {
        let diff = differenceInMinutes(
          new Date(
            values[1].format("YYYY/MM/DD") + " " + tempSelectedStartClock
          ),
          new Date(
            values[0].format("YYYY/MM/DD") + " " + tempSelectedStartClock
          )
        );

        const matchRule = DISCOUNT_RULES(locale)
          .filter((d) => diff >= d?.ms)
          .reverse()[0];

        setSmallDiscountText(
          matchRule
            ? locale === "zh-TW"
              ? `🥳選擇 ${tempSelectedStartClock} 以後可享${
                  matchRule.rate * 10
                }折優惠！`
              : `🥳Enjoy a ${Math.round(
                  (1 - matchRule.rate) * 100
                )}% discount for selections after ${tempSelectedStartClock}!`
            : locale === "zh-TW"
              ? ""
              : ""
        );
        setDiscountText("");
      }
    } else if (tempSelectedDays.length === 1) {
      setDiscountText("");
      setSmallDiscountText("");
      appStore.tempSelectedStartClock = "";
      appStore.tempSelectedEndClock = "";
    } else {
      setValues([]);
    }
  }, [
    tempSelectedDays.length,
    tempSelectedStartClock,
    tempSelectedEndClock,
    tempSelectedDays,
  ]);

  const mapDays = useMemo(() => {
    const disableDate = ({ date }) => {
      // 處理過年期間，時間在 2026/02/14 到 2026/02/22 之間的話，全部disable
      const isBetween =
        isBefore(new Date(date.format()), new Date("2026/02/23")) &&
        isAfter(new Date(date.format()), new Date("2026/02/13"));
      if (isBetween) {
        return {
          disabled: true,
          style: { color: "#ccc" },
        };
      }
    };
    return disableDate;
  }, []);

  const isAfter20 = isAfter(new Date(), new Date("2024/06/19 15:59:59"));
  const isMdUp = useBreakpointValue({ base: false, md: true });
  const isTw = locale === "zh-TW";

  const calendarImgPath = useMemo(() => {
    if (!router.isReady || isMdUp === undefined || isMdUp === null || isLoading)
      return "";

    const isAfter0116 = isAfter(new Date(), new Date("2025-01-16 00:00:00"));

    return isMdUp
      ? isTw
        ? `/images/lnyVersion/2025/calendarInfo_mobile${isAfter0116 ? "_0116" : "_1225"}.png`
        : `/images/lnyVersion/2025/calendarInfoEN_mobile${isAfter0116 ? "_0116" : "_1225"}.png`
      : isTw
        ? `/images/lnyVersion/2025/calendarInfo_mobile${isAfter0116 ? "_0116" : "_1225"}.png`
        : `/images/lnyVersion/2025/calendarInfoEN_mobile${isAfter0116 ? "_0116" : "_1225"}.png`;
  }, [isMdUp, isLoading, router.isReady]);

  // const isAfterNewYear = isAfter(new Date(), new Date("2025/02/02 23:59:59"));

  return (
    <>
      <Calendar
        range
        mapDays={mapDays}
        value={values}
        onChange={(e) => {
          if (e.length === 2) {
            if (
              isBefore(new Date(e[0].format()), new Date("2025/01/24")) &&
              isAfter(new Date(e[1].format()), new Date("2025/01/23"))
            ) {
              toastErrorAndReport(t("Calendar_over_newyear"));
              const startDate = new DateObject(e[0]);
              if (isSameDay(new Date(e[0].format()), new Date("2025/01/23"))) {
                setValues([startDate.subtract(1, "days"), e[0]]);
                return;
              } else {
                setValues([e[0], startDate.add(1, "days")]);
                return;
              }
            } else {
              setValues(e);
            }
          } else {
            setValues(e);
          }
        }}
        numberOfMonths={width >= 768 ? 2 : 1}
        minDate={minDate}
        currentDate={new DateObject().add(1, "days")}
      />

      <Flex flexDirection={{ md: "row", base: "column" }}>
        <FormControl
          paddingTop="16px"
          paddingX="20px"
          id="start_time"
          isRequired
          mb={tempSelectedDays?.length === 2 ? "0px" : "25px"}
        >
          <FormLabel>{t("pickupTime")}</FormLabel>
          {tempSelectedDays?.length === 2 ? (
            <Flex
              justifyContent="center"
              alignItems="center"
              mb={{ md: "0", base: "15px" }}
              pb={{ md: "0", base: "15px" }}
              boxShadow={{
                md: "none",
                base: "0 1px 0 0 rgba(0, 0, 0, .1)",
              }}
            >
              <Box mr="10px" flex="1">
                <Text
                  textAlign="start"
                  mr="10px"
                  fontSize="12px"
                  lineHeight="1"
                  fontWeight="700"
                >
                  {tempSelectedDays[0]?.year} {t("year")}
                </Text>
                <Flex
                  fontSize="20px"
                  lineHeight="1.2"
                  fontWeight="700"
                  alignItems="center"
                >
                  <Text fontSize="18px" mt="2px">
                    {tempSelectedDays[0]?.format(`MM/DD`)}
                  </Text>
                  <Text fontSize="15px">
                    （
                    {WEEK_DAYS_I18N(locale)[tempSelectedDays[0]?.weekDay.index]}
                    ）
                  </Text>
                </Flex>
              </Box>
              <Select
                w="120px"
                value={tempSelectedStartClock}
                outline={
                  tempSelectedStartClock === "" ? "1px solid #475aff" : ""
                }
                onChange={(e) => {
                  appStore.tempSelectedStartClock = e.target.value;
                }}
              >
                <option value="">{t("choose")}</option>
                {TIME_OPTIONS.map((o) => {
                  let disabled = true;
                  if (timeOptsUnion.includes(o)) disabled = false;

                  // KEEP: 接受急單詢問，不限制小時內不能預訂
                  // 限制小時內不能預訂
                  const disabledDatetime = addMinutes(
                    new Date(),
                    // latestOrderTimeUnion
                    0
                  );
                  if (
                    format(disabledDatetime, "yyyy/MM/dd") ===
                    tempSelectedDays[0]?.format("YYYY/MM/DD")
                  ) {
                    if (getHours(disabledDatetime) >= o) disabled = true;
                  }

                  // HOTFIX:
                  // 如果現在已經過了晚上八點，明天的 09:00、10:00 不能選
                  // 因為現在營業時間設定 06:00 ~ 20:00，那最快被車時間是 4 小時，所以 06:00 ~ 10:00 不能選
                  // if (
                  //   isTomorrow(new Date(values[0].format("YYYY/MM/DD"))) &&
                  //   getHours(new Date()) >= 20
                  // ) {
                  //   if (o === "09" || o === "10") {
                  //     disabled = true;
                  //   }
                  // }

                  return (
                    <option key={o} value={o + ":00"} disabled={disabled}>
                      {disabled ? o + `:00(${t("full")})` : o + ":00"}
                    </option>
                  );
                })}
              </Select>
            </Flex>
          ) : (
            <Text textAlign="start" fontSize="18px" opacity="0.5">
              {t("selectStartDate")}
            </Text>
          )}
        </FormControl>
        <FormControl
          id="end_time"
          isRequired
          pt="16px"
          pb="24px"
          paddingX="20px"
        >
          <FormLabel>{t("returnTime")}</FormLabel>
          {tempSelectedDays?.length === 2 ? (
            <Flex justifyContent="center" alignItems="center">
              <Box mr="10px" flex="1">
                <Text
                  textAlign="start"
                  mr="10px"
                  fontSize="12px"
                  lineHeight="1"
                  fontWeight="700"
                >
                  {tempSelectedDays[1]?.year} {t("year")}
                </Text>
                <Flex
                  fontSize="20px"
                  lineHeight="1.2"
                  fontWeight="700"
                  alignItems="center"
                >
                  <Text fontSize="18px" mt="2px">
                    {tempSelectedDays[1]?.format(`MM/DD`)}
                  </Text>
                  <Text fontSize="15px">
                    （
                    {WEEK_DAYS_I18N(locale)[tempSelectedDays[1]?.weekDay.index]}
                    ）
                  </Text>
                </Flex>
              </Box>
              <Select
                w="120px"
                value={tempSelectedEndClock}
                outline={tempSelectedEndClock === "" ? "1px solid #475aff" : ""}
                onChange={(e) => {
                  appStore.tempSelectedEndClock = e.target.value;
                }}
              >
                <option value="">{t("choose")}</option>
                {TIME_OPTIONS.map((o) => {
                  let disabled = true;
                  if (timeOptsUnion.includes(o)) disabled = false;
                  return (
                    <option key={o} value={o + ":00"} disabled={disabled}>
                      {disabled ? o + `:00(${t("full")})` : o + ":00"}
                    </option>
                  );
                })}
              </Select>
            </Flex>
          ) : (
            <Text textAlign="start" fontSize="18px" opacity="0.5">
              {t("selectEndDate")}
            </Text>
          )}

          {smallDiscountText !== "" && (
            <Box
              borderRadius="4px"
              bg="#FFEACC"
              px={{ base: "12px", md: "12px" }}
              py="4px"
              mb="24px"
              mt="12px"
            >
              <Text
                color="#FF9500"
                fontSize="12px"
                fontWeight="700"
                lineHeight="20px"
              >
                {smallDiscountText}
              </Text>
            </Box>
          )}
        </FormControl>
      </Flex>

      {/* {!isAfterNewYear && (
        <Flex>
          {isMdUp && <Flex flex="1"></Flex>}
          <Flex
            flex="1"
            mb="16px"
            w="100%"
            cursor="pointer"
            onClick={() => {
              router.push({
                pathname: "/newYear",
                query: router.query,
              });
            }}
          >
            <GCPImage
              src={`${calendarImgPath}?v=${new Date().getTime()}`}
              alt="timeSelect"
            />
          </Flex>
        </Flex>
      )} */}

      {discountText !== "" && (
        <Box
          bg="#FFEACC"
          px={{ base: "12px", md: "12px" }}
          py="8px"
          mb="24px"
          borderRadius="4px"
        >
          <Text
            textAlign="start"
            color="#FF9500"
            fontSize="12px"
            fontWeight="700"
            lineHeight="20px"
          >
            {discountText}
          </Text>
        </Box>
      )}
    </>
  );
};
export default TimeSelectorContent;
